import type { ReactNode } from "react";
import { Route, Routes, useLocation } from "react-router-dom";

import { AuthGate } from "./auth/AuthGate";
import { useSession } from "./auth/session/SessionProvider";
import { AuthRoutes } from "./features/auth/AuthRoutes";
import { isAuthPath } from "./features/auth/paths";
import { AppNav } from "./components/AppNav";
import { AppShell } from "./components/AppShell";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { AdminPage } from "./features/admin/AdminPage";
import { AdminLlmPlaygroundPage } from "./features/admin/AdminLlmPlaygroundPage";
import { AdminUserProfilePage } from "./features/admin/AdminUserProfilePage";
import { AdminPlansPage } from "./features/admin/AdminPlansPage";
import { AgentCardPage } from "./features/card/AgentCardPage";
import { BookingPagesPage } from "./features/booking/BookingPagesPage";
import { PublicBookingPage } from "./features/booking/PublicBookingPage";
import { PublicBookingManagePage } from "./features/booking/PublicBookingManagePage";
import { PublicRollingLinkPage } from "./features/booking/PublicRollingLinkPage";
import { PublicRollingLinkManagePage } from "./features/booking/PublicRollingLinkManagePage";
import { PublicPollPage } from "./features/booking/poll/PublicPollPage";
import { PublicPollManagePage } from "./features/booking/poll/PublicPollManagePage";
import { CookiePolicyPage } from "./features/legal/CookiePolicyPage";
import { PrivacyPage } from "./features/legal/PrivacyPage";
import { TermsGate } from "./features/legal/TermsGate";
import { TermsPage } from "./features/legal/TermsPage";
import { AgentRequestsPage } from "./features/agentrequests/AgentRequestsPage";
import { ConnectionsPage } from "./features/connections/ConnectionsPage";
import { ChatPage } from "./features/home/ChatPage";
import { OverviewPage } from "./features/home/OverviewPage";
import { MeetingsPage } from "./features/meetings/MeetingsPage";
import { CalendarLoginSync } from "./features/profile/CalendarLoginSync";
import { ProfileProvider, useProfile } from "./features/profile/ProfileContext";
import { ProfilePage } from "./features/profile/ProfilePage";
import { UpgradePage } from "./features/profile/UpgradePage";
import { ProfileSetupPage } from "./features/profile/ProfileSetupPage";
import { RequireFeature } from "./features/profile/RequireFeature";
import { RequireHandle } from "./features/profile/RequireHandle";
import { WaitlistPage } from "./features/waitlist/WaitlistPage";

/**
 * App shell. The ProfileProvider wraps the route tree so both the gate
 * ({@link RequireHandle}) and the setup page ({@link ProfileSetupPage})
 * share one fetched ProfileView — no duplicate GETs, and the setup page
 * can optimistically push the new ProfileView into context after a
 * successful POST so the next navigation isn't gated again.
 *
 * <p>Routing rules:</p>
 * <ul>
 *   <li>{@code /@handle} is the agent-card page. It is now GATED: only
 *       signed-in members may view a card (owner always; others per the
 *       card's visibility), so it renders INSIDE AuthGate/ProfileProvider
 *       and in the app shell like every other page. Matched by hand because
 *       react-router can't param-match a partial segment ({@code /@:handle}
 *       reads as a literal).</li>
 *   <li>{@code /profile/setup} sits OUTSIDE the handle gate — that's the
 *       one place a handle-less user is allowed to be.</li>
 *   <li>Every other route is wrapped in {@link RequireHandle} which
 *       redirects to {@code /profile/setup} when {@code handle} is null
 *       and admits the route otherwise.</li>
 * </ul>
 */
export function App() {
  const { pathname } = useLocation();
  const session = useSession();
  const signup = () => session.signIn();

  // Sign-in / sign-up / password-reset — our own pages, rendered OUTSIDE the
  // AuthGate (they are the thing an anonymous visitor is sent to, so gating
  // them would loop). In oidc mode they don't exist: sign-in is a redirect to
  // the IdP, and anything under /signin* falls through to the gate.
  if (session.mode === "cognito" && isAuthPath(pathname)) {
    return <AuthRoutes />;
  }

  // Public legal pages — rendered OUTSIDE the AuthGate/ProfileProvider so
  // anonymous visitors reach them from the landing footer.
  // onSignup is passed in (not read from useAuth inside the layout) because the
  // standalone catalog bundle renders these same pages without an OIDC provider.
  // brand="scheda": this bundle is served at scheda.delgentic.com (and the legacy
  // a2a alias), under Scheda's own landing. The apex passes "delgentic" instead.
  if (pathname === "/privacy") {
    return <PrivacyPage brand="scheda" onSignup={signup} />;
  }
  if (pathname === "/cookies") {
    return <CookiePolicyPage brand="scheda" onSignup={signup} />;
  }
  if (pathname === "/terms") {
    return <TermsPage brand="scheda" onSignup={signup} />;
  }
  // Public booking pages — an off-platform invitee books a slot with no login,
  // so this renders OUTSIDE the AuthGate/ProfileProvider (its /profile GET would
  // 401 for them), matched by hand like the agent-card route.
  // Self-service manage/cancel from the confirmation email — token-gated, no login.
  const manageMatch = /^\/book\/manage\/([^/]+)$/.exec(pathname);
  if (manageMatch) {
    return <PublicBookingManagePage token={decodeURIComponent(manageMatch[1])} />;
  }
  const bookMatch = /^\/book\/([^/]+)$/.exec(pathname);
  if (bookMatch) {
    return <PublicBookingPage slug={decodeURIComponent(bookMatch[1])} />;
  }
  // Rolling-link self-service manage/cancel/reschedule from the confirmation email — token-gated,
  // no login.
  //
  // The PATH stays /clinic/manage/ even though nothing else is called a clinic any more. It is
  // printed in every confirmation email already sitting in an invitee's inbox, so changing it
  // breaks manage links that are already out there, permanently and silently.
  // RollingLinkConfirmationMailer builds the same string; the two must not drift.
  const rollingLinkManageMatch = /^\/clinic\/manage\/([^/]+)$/.exec(pathname);
  if (rollingLinkManageMatch) {
    return <PublicRollingLinkManagePage token={decodeURIComponent(rollingLinkManageMatch[1])} />;
  }
  // Poll self-service: change your own answers from the receipt — token-gated, no login. Matched
  // BEFORE /poll/{slug}, the same order /book/manage takes, so "manage" can never be read as a slug.
  const pollManageMatch = /^\/poll\/manage\/([^/]+)$/.exec(pathname);
  if (pollManageMatch) {
    return <PublicPollManagePage token={decodeURIComponent(pollManageMatch[1])} />;
  }
  const pollMatch = /^\/poll\/([^/]+)$/.exec(pathname);
  if (pollMatch) {
    return <PublicPollPage slug={decodeURIComponent(pollMatch[1])} />;
  }
  // Rolling link /@owner/{slug} — public, anonymous, its own long-lived personal link, and now the
  // ONLY thing under /@. Ad-hoc pages and polls used to publish branded three-segment aliases here;
  // those are gone. Their random slug is the access control, and a title-derived alias beside it was
  // guessable — /@chris/ad-hoc/1-hr-meeting takes a handle plus a lucky guess at a title. A rolling
  // link is different in kind: long-lived, deliberately memorable, and this IS its address. Two
  // segments, so it's matched BEFORE the one-segment card route below and returned early.
  const rollingLinkMatch = /^\/@([^/]+)\/([^/]+)$/.exec(pathname);
  if (rollingLinkMatch) {
    return (
      <PublicRollingLinkPage
        ownerHandle={decodeURIComponent(rollingLinkMatch[1])}
        rollingLinkHandle={decodeURIComponent(rollingLinkMatch[2])}
      />
    );
  }

  // The agent card (/@handle) is gated — only signed-in members may view it — so
  // it sits INSIDE AuthGate/ProfileProvider and renders in the app shell like
  // every other page. Hand-matched because react-router can't param-match the
  // "@" partial segment.
  const cardMatch = /^\/@([^/]+)$/.exec(pathname);

  return (
    <AuthGate>
      <ProfileProvider>
        <ApprovalGate>
          {/* INSIDE ApprovalGate so a waitlisted user is not asked to agree to terms for a
              service they cannot use yet, and OUTSIDE the route tree so it also covers
              /profile/setup — a new user meets it on the first screen they see. */}
          <TermsGate>
          <ErrorBoundary>
          {/* Render-nothing: re-imports the user's Google calendar on a fresh
              login (no-op unless they have an ACTIVE connection). */}
          <CalendarLoginSync />
          {cardMatch ? (
            <RequireHandle>
              {/* translate="no" for the same reason as AppShell: this is the
                  signed-in interior, hand-rolled here because the "@" route
                  can't be param-matched. */}
              <div className="min-h-screen bg-background" translate="no">
                <AppNav />
                <AgentCardPage handle={decodeURIComponent(cardMatch[1])} />
              </div>
            </RequireHandle>
          ) : (
          <Routes>
            {/* Onboarding gate — deliberately OUTSIDE the AppShell nav: a
                handle-less user has nowhere to navigate yet. */}
            <Route path="/profile/setup" element={<ProfileSetupPage />} />
            {/* Every signed-in app page shares one header via AppShell. */}
            <Route element={<AppShell />}>
              {/* Home is the overview for EVERY tier. The A2A chat used to live here,
                  which meant a free user's first screen was a Pro-gated feature and a
                  guess (HomeGate, now gone) decided where anyone belonged. */}
              <Route
                path="/"
                element={
                  <RequireHandle>
                    <OverviewPage />
                  </RequireHandle>
                }
              />
              {/* Agent chat is Pro and above. The nav entry is hidden for everyone else, so this
                  guard is for the bookmark and the hand-typed URL. The API refuses regardless. */}
              <Route
                path="/chat"
                element={
                  <RequireHandle>
                    <RequireFeature feature="AI_SCHEDULING">
                      <ChatPage />
                    </RequireFeature>
                  </RequireHandle>
                }
              />
              <Route
                path="/meetings"
                element={
                  <RequireHandle>
                    <MeetingsPage />
                  </RequireHandle>
                }
              />
              {/* NOT behind RequireFeature, unlike /chat. Seeing who you are connected to — and
                  getting out of it — cannot be a paid feature: V35 backfilled connections nobody
                  agreed to, so the people most likely to need this page are the ones without the
                  capability. Sending a request is still Pro, gated on the endpoint and on the
                  compose box inside. */}
              <Route
                path="/connections"
                element={
                  <RequireHandle>
                    <ConnectionsPage />
                  </RequireHandle>
                }
              />
              {/* Behind RequireFeature, unlike /connections: an inbox of agent requests is empty by
                  construction for anybody no agent can reach, and its actions all 402. There is
                  nothing here for them to be shut out of, so the page is simply not theirs. */}
              <Route
                path="/agent-requests"
                element={
                  <RequireHandle>
                    <RequireFeature feature="A2A_MARKETPLACE">
                      <AgentRequestsPage />
                    </RequireFeature>
                  </RequireHandle>
                }
              />
              <Route
                path="/booking-pages"
                element={
                  <RequireHandle>
                    <BookingPagesPage />
                  </RequireHandle>
                }
              />
              <Route
                path="/profile"
                element={
                  <RequireHandle>
                    <ProfilePage />
                  </RequireHandle>
                }
              />
              <Route
                path="/upgrade"
                element={
                  <RequireHandle>
                    <UpgradePage />
                  </RequireHandle>
                }
              />
              <Route
                path="/admin"
                element={
                  <RequireHandle>
                    <AdminPage />
                  </RequireHandle>
                }
              />
              <Route
                path="/admin/users/:userId"
                element={
                  <RequireHandle>
                    <AdminUserProfilePage />
                  </RequireHandle>
                }
              />
              <Route
                path="/admin/llm"
                element={
                  <RequireHandle>
                    <AdminLlmPlaygroundPage />
                  </RequireHandle>
                }
              />
              <Route
                path="/admin/plans"
                element={
                  <RequireHandle>
                    <AdminPlansPage />
                  </RequireHandle>
                }
              />
            </Route>
          </Routes>
          )}
          </ErrorBoundary>
          </TermsGate>
        </ApprovalGate>
      </ProfileProvider>
    </AuthGate>
  );
}

/**
 * The approval gate in the SPA. Once the profile is loaded, an unapproved user
 * (DISABLED, or PENDING if the sign-up gate has been switched back on, and not
 * an admin) sees the waitlist screen instead of any app route — including
 * /profile/setup, so a gated user can't onboard ahead of approval. The backend
 * enforces the same gate on the API, so this is UX, not security.
 *
 * <p>Note this reads `approved`, not `approvalStatus === "pending"`. New
 * sign-ups are approved on arrival now, so in practice this catches accounts an
 * admin has disabled — which is exactly why the check must stay put rather than
 * being deleted along with the waitlist.</p>
 *
 * <p>While the profile is still loading we hold the whole app back rather than
 * mounting it optimistically. We can't know approval yet, and some children
 * fetch gated endpoints the instant they mount — the header's ConnectionsBell
 * (/connections/received/count) and CalendarLoginSync (/integrations) both do,
 * and they sit above each route's RequireHandle spinner so that gate never
 * covers them. Mounting first means a fresh, still-provisioning (unapproved)
 * user sprays 403s before the waitlist verdict lands. On error we fall through
 * so RequireHandle can surface its retry UI.</p>
 */
function ApprovalGate({ children }: { children: ReactNode }) {
  const { state } = useProfile();
  if (state.status === "loading") {
    return (
      <div className="mx-auto max-w-md p-10 text-center font-comic-body">
        <p className="font-comic-headline text-sm uppercase tracking-wider text-muted-foreground">
          Loading your profile…
        </p>
      </div>
    );
  }
  if (state.status === "ready" && !state.profile.approved) {
    return <WaitlistPage status={state.profile.approvalStatus} />;
  }
  return <>{children}</>;
}
